import { Card, Suit, Player } from '../types';
import { GAME_CONSTANTS } from './constants';

export const PRIMIERA_VALUES: Record<number, number> = {
  7: 21,
  6: 18,
  1: 16,
  5: 15,
  4: 14,
  3: 13,
  2: 12,
  8: 10,
  9: 10,
  10: 10,
};

export function createDeck(): Card[] {
  const deck: Card[] = [];
  const suits = [Suit.COINS, Suit.CUPS, Suit.SWORDS, Suit.CLUBS];
  
  for (const suit of suits) {
    for (let value = 1; value <= 10; value++) {
      deck.push({
        id: `${suit}-${value}`,
        value,
        suit,
      });
    }
  }
  return deck;
}

export function shuffle<T>(array: T[]): T[] {
  const newArray = [...array];
  for (let i = newArray.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [newArray[i], newArray[j]] = [newArray[j], newArray[i]];
  }
  return newArray;
}

/** Create players array for a new/reset game. Used by both AI and online modes. */
export function createPlayers(deck: Card[], numPlayers: number): Player[] {
  const players: Player[] = [
    {
      id: 0,
      name: 'Tu',
      hand: deck.splice(0, 3),
      captured: [],
      currentRoundScore: 0,
      totalScore: 0,
      scope: 0,
      hasDeclaredLowHand: false,
      hasDeclaredThreeAces: false,
      lowHandCount: 0,
      threeAcesCount: 0,
    },
  ];

  const numAI = numPlayers - 1;
  for (let i = 1; i <= numAI; i++) {
    players.push({
      id: i,
      name: i === 1 ? 'CPU' : `CPU ${i}`,
      hand: deck.splice(0, 3),
      captured: [],
      currentRoundScore: 0,
      totalScore: 0,
      scope: 0,
      hasDeclaredLowHand: false,
      hasDeclaredThreeAces: false,
      lowHandCount: 0,
      threeAcesCount: 0,
    });
  }

  return players;
}

export function getPossibleCaptures(playedCard: Card, table: Card[] = []): Card[][] {
  const safeTable = table || [];
  if (safeTable.length === 0) {
    if (playedCard.value === 1) return [[]]; // Ace takes itself on empty table
    return [];
  }
  
  // Rule: Asso piglia solo l'asso a terra (se c'è), non tutto
  if (playedCard.value === 1) {
    const aceOnTable = safeTable.find(c => c.value === 1);
    if (aceOnTable) {
      // If there is an ace on the table, it takes only that ace
      return [[aceOnTable]];
    }
    // No ace on table, take everything
    return [[...safeTable]];
  }

  const target = playedCard.value;
  const directMatches = safeTable.filter(c => c.value === target).map(c => [c]);
  if (directMatches.length > 0) {
    return directMatches;
  }
  
  // Combination captures
  const combinationCaptures: Card[][] = [];
  
  function findCombinations(startIndex: number, currentSum: number, currentCombination: Card[]) {
    if (currentSum === target) {
      if (currentCombination.length >= 2) {
        combinationCaptures.push([...currentCombination]);
      }
      return;
    }
    if (currentSum > target || startIndex >= safeTable.length) {
      return;
    }
    
    for (let i = startIndex; i < safeTable.length; i++) {
      findCombinations(
        i + 1, 
        currentSum + safeTable[i].value, 
        [...currentCombination, safeTable[i]]
      );
    }
  }
  
  findCombinations(0, 0, []);
  
  // Return ALL valid captures (direct + combinations) — player/AI chooses
  return [...directMatches, ...combinationCaptures];
}

/** Determine if a move is a scopa (captured all cards on the table).
 *  For Aces: scopa ONLY if the table has exactly one Ace and nothing else.
 *  Ace sweeping a multi-card table (no Ace present) is NOT a scopa. */
export function isScopaMove(playedCard: Card, table: Card[], capturedCards: Card[]): boolean {
  if (table.length === 0) return false;
  if (playedCard.value === 1) {
    return table.length === 1 && table[0].value === 1;
  }
  return capturedCards.length === table.length;
}

export interface PlayerRoundResult {
  cards: number;
  coins: number;
  settebello: boolean;
  dieciDenari: boolean;
  dueDenari: boolean;
  primiera: number;
  scope: number;
  lowHand: number;
  threeAces: number;
  score: number;
}

export interface RoundScoreResults {
  results: PlayerRoundResult[];
  totalScores: number[];
}

export function calculateRoundScores(
  players: Player[],
  numPlayers: number
): RoundScoreResults {
  if (numPlayers === 4) {
    const teamAPlayer: Player = {
      id: 0,
      name: 'Team A',
      hand: [],
      captured: [...(players[0]?.captured || []), ...(players[2]?.captured || [])],
      currentRoundScore: 0,
      totalScore: 0,
      scope: (players[0]?.scope || 0) + (players[2]?.scope || 0),
      hasDeclaredLowHand: (players[0]?.hasDeclaredLowHand || players[2]?.hasDeclaredLowHand || false),
      hasDeclaredThreeAces: (players[0]?.hasDeclaredThreeAces || players[2]?.hasDeclaredThreeAces || false),
      lowHandCount: (players[0]?.lowHandCount || 0) + (players[2]?.lowHandCount || 0),
      threeAcesCount: (players[0]?.threeAcesCount || 0) + (players[2]?.threeAcesCount || 0),
    };

    const teamBPlayer: Player = {
      id: 1,
      name: 'Team B',
      hand: [],
      captured: [...(players[1]?.captured || []), ...(players[3]?.captured || [])],
      currentRoundScore: 0,
      totalScore: 0,
      scope: (players[1]?.scope || 0) + (players[3]?.scope || 0),
      hasDeclaredLowHand: (players[1]?.hasDeclaredLowHand || players[3]?.hasDeclaredLowHand || false),
      hasDeclaredThreeAces: (players[1]?.hasDeclaredThreeAces || players[3]?.hasDeclaredThreeAces || false),
      lowHandCount: (players[1]?.lowHandCount || 0) + (players[3]?.lowHandCount || 0),
      threeAcesCount: (players[1]?.threeAcesCount || 0) + (players[3]?.threeAcesCount || 0),
    };

    const teamResults = calculateRoundScores([teamAPlayer, teamBPlayer], 2);
    const teamARes = teamResults.results[0];
    const teamBRes = teamResults.results[1];

    const results: PlayerRoundResult[] = [
      { ...teamARes },
      { ...teamBRes },
      { ...teamARes },
      { ...teamBRes },
    ];

    const totalScores = [
      teamResults.totalScores[0],
      teamResults.totalScores[1],
      teamResults.totalScores[0],
      teamResults.totalScores[1],
    ];

    return { results, totalScores };
  }

  // Calculate per-player base stats
  const baseStats = players.slice(0, numPlayers).map(player => ({
    cards: player.captured.length,
    coins: player.captured.filter(c => c.suit === Suit.COINS).length,
    settebello: player.captured.some(c => c.suit === Suit.COINS && c.value === 7),
    dieciDenari: player.captured.some(c => c.suit === Suit.COINS && c.value === 10),
    dueDenari: player.captured.some(c => c.suit === Suit.COINS && c.value === 2),
    primiera: calculatePrimiera(player.captured),
    scope: player.scope,
    lowHand: (player.lowHandCount || 0) * GAME_CONSTANTS.SBARAZZO_POINTS,
    threeAces: (player.threeAcesCount || 0) * GAME_CONSTANTS.THREE_ACES_POINTS,
  }));

  // Start with scope + low hand + three aces
  const scores = baseStats.map(s => s.scope + s.lowHand + s.threeAces);

  // Most Cards: +1 to player with most captured cards (tie = no point)
  const maxCards = Math.max(...baseStats.map(s => s.cards));
  const cardsWinners = baseStats.filter(s => s.cards === maxCards);
  if (cardsWinners.length === 1 && maxCards > 0) {
    scores[baseStats.findIndex(s => s.cards === maxCards)] += GAME_CONSTANTS.MOST_CARDS_BONUS;
  }

  // Most Coins: +1 to player with most coins (tie = no point)
  const maxCoins = Math.max(...baseStats.map(s => s.coins));
  const coinsWinners = baseStats.filter(s => s.coins === maxCoins);
  if (coinsWinners.length === 1 && maxCoins > 0) {
    scores[baseStats.findIndex(s => s.coins === maxCoins)] += GAME_CONSTANTS.MOST_COINS_BONUS;
  }

  // Settebello: +1 each
  baseStats.forEach((s, i) => {
    if (s.settebello) scores[i] += GAME_CONSTANTS.SETTEBELLO_BONUS;
  });

  // Primiera: +1 to player with highest primiera (tie = no point)
  const maxPrimiera = Math.max(...baseStats.map(s => s.primiera));
  const primieraWinners = baseStats.filter(s => s.primiera === maxPrimiera);
  if (primieraWinners.length === 1 && maxPrimiera > 0) {
    scores[baseStats.findIndex(s => s.primiera === maxPrimiera)] += GAME_CONSTANTS.PRIMIERA_BONUS;
  }

  // 10 of Coins: +1 each
  baseStats.forEach((s, i) => {
    if (s.dieciDenari) scores[i] += GAME_CONSTANTS.DIECI_DENARI_BONUS;
  });

  // 2 of Coins: -5 each
  baseStats.forEach((s, i) => {
    if (s.dueDenari) scores[i] += GAME_CONSTANTS.DUE_DENARI_PENALTY;
  });

  // Zero coins penalty: -10 if captured cards but no coins
  baseStats.forEach((s, i) => {
    if (s.coins === 0 && s.cards > 0) scores[i] += GAME_CONSTANTS.ZERO_COINS_PENALTY;
  });

  const results: PlayerRoundResult[] = baseStats.map((s, i) => ({
    ...s,
    score: scores[i],
  }));

  return { results, totalScores: scores };
}

function calculatePrimiera(captured: Card[]): number {
  const bestBySuit: Record<Suit, number> = {
    [Suit.COINS]: 0,
    [Suit.CUPS]: 0,
    [Suit.SWORDS]: 0,
    [Suit.CLUBS]: 0,
  };

  for (const card of captured) {
    const value = PRIMIERA_VALUES[card.value] || 0;
    if (value > bestBySuit[card.suit]) {
      bestBySuit[card.suit] = value;
    }
  }

  const values = Object.values(bestBySuit);
  return values.reduce((sum, v) => sum + v, 0);
}

export function getHandSum(hand: Card[]): number {
  return hand.reduce((sum, c) => sum + (c.value > 10 ? 10 : c.value), 0);
}